/**
 * Enrichment API
 *
 * Functions for AI-powered task enrichment during work item creation.
 *
 * All functions implement graceful degradation - errors are caught and
 * converted to empty responses rather than throwing, ensuring the UI
 * remains functional even when AI enrichment is unavailable.
 */

import { api, type AiServed } from "./client";
import type {
  ProjectId,
  DraftEnrichmentRequest,
  DraftEnrichmentResponse,
  Tier2PollResponse,
} from "./types";

/**
 * Request draft enrichment for a work item before creation.
 *
 * Returns Tier 1 suggestions immediately (~100-200ms).
 * If include_tier2 is true, starts background processing for deeper analysis.
 *
 * Implements graceful degradation: if the API call fails, returns an empty
 * response with error message instead of throwing.
 *
 * @param projectId - Project context for enrichment
 * @param request - Title and optional description to analyze
 * @returns Tier 1 results immediately, with enrichment_id for Tier 2 polling
 */
export async function enrichDraft(
  projectId: ProjectId,
  request: DraftEnrichmentRequest,
): Promise<AiServed<DraftEnrichmentResponse>> {
  try {
    return await api.postAi<DraftEnrichmentResponse>(
      `/projects/${projectId}/work-items/enrich-draft`,
      request,
    );
  } catch {
    // Graceful degradation: return empty response instead of throwing
    return {
      tier1: undefined,
      tier2: undefined,
      enrichment_id: crypto.randomUUID(),
      tier2_status: "failed",
      error: "AI suggestions temporarily unavailable",
      degraded: false,
    };
  }
}

/**
 * Poll for Tier 2 enrichment results.
 *
 * Call this periodically after enrichDraft returns with tier2_status: "processing".
 *
 * Implements graceful degradation: if the API call fails, returns a failed
 * status instead of throwing.
 *
 * @param enrichmentId - ID returned from enrichDraft response
 * @returns Current status and Tier 2 results when complete
 */
export async function pollTier2Enrichment(
  enrichmentId: string,
): Promise<Tier2PollResponse> {
  try {
    return await api.get<Tier2PollResponse>(
      `/enrichment/${enrichmentId}/tier2`,
    );
  } catch {
    // Graceful degradation: return failed status instead of throwing
    return {
      status: "failed",
      tier2: undefined,
      error: "Failed to fetch enrichment results",
    };
  }
}
